import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import { features, useCurrentUser, useThunkDispatch } from '@tunasong/redux'
import type { Agent, Comment, Persisted } from '@tunasong/schemas'
import { isInUsersAgentChat } from '@tunasong/schemas'
import { startNewConversation } from './start-new-conversation.js'
import { useAgentTurnStream } from './use-agent-turn-stream.js'

/**
 * The panel's "New conversation" (Chat threads per advisor): offered once the user has a conversation with this
 * agent to start over from. Disabled, not hidden, while the agent is answering: a reply landing after the cutoff
 * would open the new conversation with an answer to the old one, and hiding it would make the header jump and
 * drop focus (Joni, #2523). Same action as the palette Command (`startNewConversation`).
 */
export const useNewConversation = (agent: Persisted<Agent>) => {
  const { userId } = useCurrentUser()
  const dispatch = useThunkDispatch()
  // Same call shape as AgentChat's, so it shares the RTK Query cache entry
  const { entities: comments = [] } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agent.id,
    filter: 'comment',
  })
  const liveStream = useAgentTurnStream(agent.id)

  const isOffered = Boolean(userId) && comments.some(c => isInUsersAgentChat(c, agent, userId ?? ''))
  const isDisabled = Boolean(liveStream)

  const start = async () => {
    if (!userId || isDisabled) {
      return
    }
    try {
      await startNewConversation({ dispatch, agent, userId })
    } catch (error) {
      logger.error('agent: could not start a new conversation', error)
      dispatch(
        features.notifications.actions.setAlert({
          message: 'Couldn’t start a new conversation. Try again.',
          severity: 'error',
        })
      )
    }
  }

  return { isOffered, isDisabled, start }
}
